import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';

const AdminSystem = () => {
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState({
    status: 'healthy',
    uptime: '0 days, 0 hours',
    lastBackup: 'Never',
    databaseSize: '0 MB',
    activeConnections: 0,
    errorRate: '0%'
  });
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    autoBackup: true,
    backupFrequency: 'daily',
    maxFileSize: '10MB',
    sessionTimeout: 30,
    emailNotifications: true,
    debugMode: false
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [backupHistory, setBackupHistory] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);

  useEffect(() => {
    if (currentUser && userData) {
      loadSystemData();
    }
  }, [currentUser, userData]);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      
      // Simulate loading system data
      // In a real application, these would be API calls
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock system health data
      setSystemHealth({
        status: 'healthy',
        uptime: '15 days, 8 hours',
        lastBackup: '2 hours ago',
        databaseSize: '2.4 GB',
        activeConnections: 47,
        errorRate: '0.02%'
      });

      // Mock recent logs
      setRecentLogs([
        { id: 1, level: 'info', message: 'System backup completed successfully', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), source: 'backup-service' },
        { id: 2, level: 'info', message: 'User authentication successful', timestamp: new Date(Date.now() - 5 * 60 * 1000), source: 'auth-service' },
        { id: 3, level: 'warning', message: 'High memory usage detected', timestamp: new Date(Date.now() - 15 * 60 * 1000), source: 'monitoring' },
        { id: 4, level: 'error', message: 'Database connection timeout', timestamp: new Date(Date.now() - 30 * 60 * 1000), source: 'database' },
        { id: 5, level: 'info', message: 'Scheduled maintenance completed', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), source: 'maintenance' }
      ]);

      // Mock backup history
      setBackupHistory([
        { id: 1, type: 'full', status: 'completed', size: '2.4 GB', duration: '15 min', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000) },
        { id: 2, type: 'incremental', status: 'completed', size: '150 MB', duration: '3 min', timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000) },
        { id: 3, type: 'full', status: 'completed', size: '2.3 GB', duration: '14 min', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000) },
        { id: 4, type: 'incremental', status: 'failed', size: '0 MB', duration: '0 min', timestamp: new Date(Date.now() - 30 * 60 * 60 * 1000) }
      ]);

      // Mock maintenance tasks
      setMaintenanceTasks([
        { id: 1, name: 'Database optimization', status: 'completed', priority: 'medium', estimatedDuration: '30 min', lastRun: new Date(Date.now() - 24 * 60 * 60 * 1000) },
        { id: 2, name: 'Log cleanup', status: 'scheduled', priority: 'low', estimatedDuration: '10 min', lastRun: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
        { id: 3, name: 'Security scan', status: 'running', priority: 'high', estimatedDuration: '45 min', lastRun: new Date(Date.now() - 2 * 60 * 60 * 1000) },
        { id: 4, name: 'Performance monitoring', status: 'scheduled', priority: 'medium', estimatedDuration: '20 min', lastRun: new Date(Date.now() - 12 * 60 * 60 * 1000) }
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error loading system data:', error);
      setLoading(false);
    }
  };

  const handleSettingChange = (setting, value) => {
    setSystemSettings(prev => ({
      ...prev,
      [setting]: value
    }));
    
    // In a real application, this would save to the backend
    console.log(`Setting ${setting} changed to:`, value);
  };

  const triggerBackup = async () => {
    try {
      // Simulate backup process
      console.log('Starting manual backup...');
      // In a real application, this would trigger a backup API call
      alert('Backup process initiated. You will be notified when it completes.');
    } catch (error) {
      console.error('Error triggering backup:', error);
      alert('Failed to trigger backup. Please try again.');
    }
  };

  const runMaintenanceTask = async (taskId) => {
    try {
      console.log(`Running maintenance task: ${taskId}`);
      // In a real application, this would trigger the maintenance task
      alert('Maintenance task started. You will be notified when it completes.');
    } catch (error) {
      console.error('Error running maintenance task:', error);
      alert('Failed to run maintenance task. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'running': return 'text-blue-600 bg-blue-100';
      case 'scheduled': return 'text-purple-600 bg-purple-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'info': return 'text-blue-600 bg-blue-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading system information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">System</h1>
                <p className="text-sm text-gray-500 mt-0.5">Health, settings, and maintenance</p>
              </div>
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </header>
          <div className="p-8">

        {/* System Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">System Status</p>
                <p className={`text-2xl font-bold ${getStatusColor(systemHealth.status)} px-2 py-1 rounded-full text-sm`}>
                  {systemHealth.status.toUpperCase()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Uptime</p>
                <p className="text-2xl font-bold text-gray-900">{systemHealth.uptime}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Last Backup</p>
                <p className="text-2xl font-bold text-gray-900">{systemHealth.lastBackup}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Database Size</p>
                <p className="text-2xl font-bold text-gray-900">{systemHealth.databaseSize}</p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Connections</p>
                <p className="text-2xl font-bold text-gray-900">{systemHealth.activeConnections}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900">{systemHealth.errorRate}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">System Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Maintenance Mode</label>
                <button
                  onClick={() => handleSettingChange('maintenanceMode', !systemSettings.maintenanceMode)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    systemSettings.maintenanceMode ? 'bg-red-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    systemSettings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Auto Backup</label>
                <button
                  onClick={() => handleSettingChange('autoBackup', !systemSettings.autoBackup)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    systemSettings.autoBackup ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    systemSettings.autoBackup ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                <button
                  onClick={() => handleSettingChange('emailNotifications', !systemSettings.emailNotifications)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    systemSettings.emailNotifications ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    systemSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
                <select
                  value={systemSettings.backupFrequency}
                  onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
                <input
                  type="number"
                  value={systemSettings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="5"
                  max="480"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max File Size</label>
                <select
                  value={systemSettings.maxFileSize}
                  onChange={(e) => handleSettingChange('maxFileSize', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="5MB">5 MB</option>
                  <option value="10MB">10 MB</option>
                  <option value="25MB">25 MB</option>
                  <option value="50MB">50 MB</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={triggerBackup}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Trigger Manual Backup
            </button>
            <button
              onClick={loadSystemData}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refresh Data
            </button>
          </div>
        </div>

        {/* Maintenance Tasks */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Maintenance Tasks</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Run</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {maintenanceTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {task.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        task.priority === 'high' ? 'bg-red-100 text-red-800' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {task.estimatedDuration}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {task.lastRun.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {task.status === 'scheduled' && (
                        <button
                          onClick={() => runMaintenanceTask(task.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Run Now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Logs and Backup History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Recent Logs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Recent System Logs</h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {recentLogs.map((log) => (
                <div key={log.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLogLevelColor(log.level)}`}>
                    {log.level}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{log.message}</p>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                      <span>{log.timestamp.toLocaleString()}</span>
                      <span>•</span>
                      <span>{log.source}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Backup History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Backup History</h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {backupHistory.map((backup) => (
                <div key={backup.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(backup.status)}`}>
                      {backup.status}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{backup.type} Backup</p>
                      <p className="text-xs text-gray-500">{backup.timestamp.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{backup.size}</p>
                    <p className="text-xs text-gray-500">{backup.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystem;
